import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
  Switch,
  TextInput,
  Alert,
  Modal,
  Platform,
  KeyboardAvoidingView,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import LottieView from "lottie-react-native";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { getAllLotties } from "@/lib/lottie-registry";
import { logActivity } from "@/lib/activity-log";

/**
 * Notification template type
 */
interface NotificationTemplate {
  id: string;
  notification_type: string;
  title: string;
  body: string;
  lottie_animation: string;
  color: string;
  is_enabled: boolean;
}

/**
 * Default notification templates
 */
const DEFAULT_TEMPLATES: Omit<NotificationTemplate, "id">[] = [
  {
    notification_type: "familie_dag_1month",
    title: "📅 Familie Dag over 1 maand!",
    body: "Over een maand is het zover! De Familie Dag komt eraan.",
    lottie_animation: "confetti",
    color: "#7C3AED",
    is_enabled: true,
  },
  {
    notification_type: "familie_dag_1week",
    title: "📅 Familie Dag over 1 week!",
    body: "Nog maar een week tot de Familie Dag! Zet hem in je agenda.",
    lottie_animation: "party",
    color: "#7C3AED",
    is_enabled: true,
  },
  {
    notification_type: "familie_dag_1day",
    title: "🎉 Morgen is de Familie Dag!",
    body: "Morgen is het zover! We zien je bij de Familie Dag.",
    lottie_animation: "cheers",
    color: "#7C3AED",
    is_enabled: true,
  },
  {
    notification_type: "familie_dag_today",
    title: "🎊 Vandaag is de Familie Dag!",
    body: "Het is zover! Vandaag vieren we de Familie Dag samen.",
    lottie_animation: "confetti",
    color: "#7C3AED",
    is_enabled: true,
  },
  {
    notification_type: "weekend_1month",
    title: "🏕️ Familie Weekend over 1 maand!",
    body: "Over een maand gaan we er samen op uit! Het Familie Weekend komt eraan.",
    lottie_animation: "party",
    color: "#10B981",
    is_enabled: true,
  },
  {
    notification_type: "weekend_1week",
    title: "🏕️ Familie Weekend over 1 week!",
    body: "Nog maar een week! Pak je tas alvast in voor het Familie Weekend.",
    lottie_animation: "balloons",
    color: "#10B981",
    is_enabled: true,
  },
  {
    notification_type: "weekend_1day",
    title: "🎒 Morgen begint het Familie Weekend!",
    body: "Morgen gaat het gebeuren! Het Familie Weekend staat voor de deur.",
    lottie_animation: "cheers",
    color: "#10B981",
    is_enabled: true,
  },
  {
    notification_type: "weekend_today",
    title: "🎉 Vandaag begint het Familie Weekend!",
    body: "Het wachten is voorbij! Vandaag start het Familie Weekend.",
    lottie_animation: "confetti",
    color: "#10B981",
    is_enabled: true,
  },
  {
    notification_type: "birthday",
    title: "🎂 Vandaag jarig: {name}!",
    body: "Vergeet niet {name} te feliciteren met hun verjaardag!",
    lottie_animation: "cake",
    color: "#F59E0B",
    is_enabled: true,
  },
];

/**
 * Available Lottie animations - uses central registry
 */
const LOTTIE_OPTIONS = getAllLotties().map((l) => ({
  id: l.key,
  name: l.name,
  file: l.source,
}));

/**
 * Color options for notifications
 */
const COLOR_OPTIONS = [
  { id: "#7C3AED", name: "Paars" },
  { id: "#10B981", name: "Groen" },
  { id: "#F59E0B", name: "Oranje" },
  { id: "#3B82F6", name: "Blauw" },
  { id: "#EF4444", name: "Rood" },
  { id: "#EC4899", name: "Roze" },
];

/**
 * Get readable name for notification type
 */
const getNotificationTypeName = (type: string): string => {
  const names: Record<string, string> = {
    familie_dag_1month: "Familie Dag - 1 maand van tevoren",
    familie_dag_1week: "Familie Dag - 1 week van tevoren",
    familie_dag_1day: "Familie Dag - 1 dag van tevoren",
    familie_dag_today: "Familie Dag - Op de dag zelf",
    weekend_1month: "Familie Weekend - 1 maand van tevoren",
    weekend_1week: "Familie Weekend - 1 week van tevoren",
    weekend_1day: "Familie Weekend - 1 dag van tevoren",
    weekend_today: "Familie Weekend - Op de dag zelf",
    birthday: "Verjaardagen",
  };
  return names[type] || type;
};

/**
 * Get icon for notification type
 */
const getNotificationTypeIcon = (type: string): keyof typeof Ionicons.glyphMap => {
  if (type.startsWith("familie_dag")) return "sunny";
  if (type.startsWith("weekend")) return "bed";
  if (type === "birthday") return "gift";
  return "notifications";
};

/**
 * Familie Notificaties Screen
 * Allows admins to manage automatic notification templates
 */
export default function FamilieNotificatiesScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { activeWeekend, user, profile } = useAuth();

  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Edit form state
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editLottie, setEditLottie] = useState("confetti");
  const [editColor, setEditColor] = useState("#7C3AED");
  const [isSaving, setIsSaving] = useState(false);

  // Broadcast notification state
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastTitle, setBroadcastTitle] = useState("");
  const [broadcastBody, setBroadcastBody] = useState("");
  const [broadcastLottie, setBroadcastLottie] = useState("Partying_Emoji");
  const [isSendingBroadcast, setIsSendingBroadcast] = useState(false);

  /**
   * Fetch notification templates
   */
  const fetchTemplates = useCallback(async () => {
    if (!activeWeekend?.id) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("notification_templates")
        .select("*")
        .eq("weekend_id", activeWeekend.id)
        .order("notification_type");

      if (error) throw error;

      // If no templates exist, create defaults
      if (!data || data.length === 0) {
        await createDefaultTemplates();
      } else {
        setTemplates(data);
      }
    } catch (err) {
      console.error("Error fetching templates:", err);
    } finally {
      setIsLoading(false);
    }
  }, [activeWeekend?.id]);

  /**
   * Create default notification templates
   */
  const createDefaultTemplates = async () => {
    if (!activeWeekend?.id) return;

    try {
      const templatesWithWeekend = DEFAULT_TEMPLATES.map((t) => ({
        ...t,
        weekend_id: activeWeekend.id,
      }));

      const { data, error } = await supabase
        .from("notification_templates")
        .insert(templatesWithWeekend)
        .select();

      if (error) throw error;
      setTemplates(data || []);
    } catch (err) {
      console.error("Error creating default templates:", err);
    }
  };

  /**
   * Toggle template enabled state
   */
  const handleToggleEnabled = async (template: NotificationTemplate) => {
    try {
      const { error } = await supabase
        .from("notification_templates")
        .update({ is_enabled: !template.is_enabled })
        .eq("id", template.id);

      if (error) throw error;

      setTemplates((prev) =>
        prev.map((t) =>
          t.id === template.id ? { ...t, is_enabled: !t.is_enabled } : t
        )
      );
    } catch (err) {
      console.error("Error toggling template:", err);
      Alert.alert("Fout", "Kon instelling niet wijzigen");
    }
  };

  /**
   * Open edit modal for a template
   */
  const handleEdit = (template: NotificationTemplate) => {
    setEditingTemplate(template);
    setEditTitle(template.title);
    setEditBody(template.body);
    setEditLottie(template.lottie_animation);
    setEditColor(template.color);
    setShowEditModal(true);
  };

  /**
   * Save template changes
   */
  const handleSave = async () => {
    if (!editingTemplate) return;

    if (!editTitle.trim() || !editBody.trim()) {
      Alert.alert("Fout", "Titel en bericht zijn verplicht");
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("notification_templates")
        .update({
          title: editTitle.trim(),
          body: editBody.trim(),
          lottie_animation: editLottie,
          color: editColor,
          updated_at: new Date().toISOString(),
        })
        .eq("id", editingTemplate.id);

      if (error) throw error;

      setTemplates((prev) =>
        prev.map((t) =>
          t.id === editingTemplate.id
            ? {
                ...t,
                title: editTitle.trim(),
                body: editBody.trim(),
                lottie_animation: editLottie,
                color: editColor,
              }
            : t
        )
      );

      setShowEditModal(false);
      setEditingTemplate(null);
    } catch (err) {
      console.error("Error saving template:", err);
      Alert.alert("Fout", "Kon wijzigingen niet opslaan");
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle pull-to-refresh
   */
  const onRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchTemplates();
    setIsRefreshing(false);
  }, [fetchTemplates]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  /**
   * Send broadcast notification to all users
   */
  const handleSendBroadcast = async () => {
    if (!broadcastTitle.trim() || !broadcastBody.trim()) {
      Alert.alert("Fout", "Titel en bericht zijn verplicht");
      return;
    }

    if (!user?.id) {
      Alert.alert("Fout", "Je moet ingelogd zijn om te versturen");
      return;
    }

    Alert.alert(
      "Notificatie versturen",
      "Weet je zeker dat je deze notificatie naar alle gebruikers wilt versturen?",
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Versturen",
          style: "default",
          onPress: async () => {
            setIsSendingBroadcast(true);
            try {
              // Get the current session for auth token
              const { data: { session } } = await supabase.auth.getSession();
              
              if (!session?.access_token) {
                throw new Error("Geen geldige sessie gevonden");
              }

              // Call the Edge Function
              const response = await fetch(
                `https://sbqngpppxrjndqkoubrw.supabase.co/functions/v1/send-broadcast-notification`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${session.access_token}`,
                  },
                  body: JSON.stringify({
                    title: broadcastTitle.trim(),
                    body: broadcastBody.trim(),
                    lottie_animation: broadcastLottie,
                    sender_id: user.id,
                    weekend_id: activeWeekend?.id || null,
                  }),
                }
              );

              const result = await response.json();

              if (!response.ok) {
                throw new Error(result.error || "Failed to send notification");
              }

              // Log the activity for super admins
              await logActivity({
                user_id: user.id,
                action_type: "notification.broadcast",
                action_category: "notification",
                target_type: "notification",
                target_name: broadcastTitle.trim(),
                metadata: {
                  title: broadcastTitle.trim(),
                  body: broadcastBody.trim(),
                  lottie_animation: broadcastLottie,
                  in_app_sent: result.in_app_sent,
                  push_sent: result.push_sent,
                  sender_name: profile?.full_name,
                },
                weekend_id: activeWeekend?.id,
              });

              Alert.alert(
                "Verstuurd! 🎉",
                `Notificatie verstuurd naar ${result.in_app_sent} gebruikers.\n${result.push_sent} push notificaties verzonden.`
              );

              // Reset form
              setBroadcastTitle("");
              setBroadcastBody("");
              setBroadcastLottie("Partying_Emoji");
              setShowBroadcastModal(false);
            } catch (err) {
              console.error("Error sending broadcast:", err);
              Alert.alert("Fout", "Kon notificatie niet versturen. Probeer het opnieuw.");
            } finally {
              setIsSendingBroadcast(false);
            }
          },
        },
      ]
    );
  };

  /**
   * Get Lottie source for animation id
   */
  const getLottieSource = (id: string) => {
    return LOTTIE_OPTIONS.find((o) => o.id === id)?.file;
  };

  /**
   * Group templates by category
   */
  const groupedTemplates = {
    familieDag: templates.filter((t) => t.notification_type.startsWith("familie_dag")),
    weekend: templates.filter((t) => t.notification_type.startsWith("weekend")),
    birthday: templates.filter((t) => t.notification_type === "birthday"),
  };

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <ActivityIndicator size="large" color={colors.primary} />
        <Text className="mt-4" style={{ color: colors.textSecondary }}>
          Laden...
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["bottom"]}>
      <ScrollView
        className="flex-1"
        contentContainerClassName="px-4 py-6"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={onRefresh}
            tintColor={colors.primary}
            colors={[colors.primary]}
          />
        }
      >
        {/* Send Broadcast Button */}
        <TouchableOpacity
          onPress={() => setShowBroadcastModal(true)}
          className="rounded-xl p-4 mb-4 flex-row items-center"
          style={{
            backgroundColor: "#10B981",
          }}
          accessibilityLabel="Stuur een notificatie naar alle gebruikers"
          accessibilityRole="button"
        >
          <View
            className="w-12 h-12 rounded-full items-center justify-center mr-4"
            style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
          >
            <Ionicons name="megaphone" size={24} color="#FFFFFF" />
          </View>
          <View className="flex-1">
            <Text className="font-bold text-white text-lg">
              Stuur notificatie
            </Text>
            <Text className="text-white/80 text-sm">
              Bereik alle familieleden direct
            </Text>
          </View>
          <View
            className="px-3 py-2 rounded-lg"
            style={{ backgroundColor: "rgba(255,255,255,0.25)" }}
          >
            <Ionicons name="send" size={18} color="#FFFFFF" />
          </View>
        </TouchableOpacity>

        {/* Header Info */}
        <View
          className="rounded-xl p-4 mb-4"
          style={{ backgroundColor: colors.primaryLight }}
        >
          <View className="flex-row items-center mb-2">
            <Ionicons name="information-circle" size={20} color={colors.primary} />
            <Text className="ml-2 font-medium" style={{ color: colors.primary }}>
              Automatische notificaties
            </Text>
          </View>
          <Text className="text-sm" style={{ color: colors.textSecondary }}>
            Deze meldingen worden automatisch verstuurd op basis van de ingestelde datums.
            Pas de tekst, kleur en animatie aan naar wens.
          </Text>
        </View>

        {/* Notification Settings Link */}
        <TouchableOpacity
          onPress={() => router.push("/(admin)/notifications/settings")}
          className="rounded-xl p-4 mb-6 flex-row items-center justify-between"
          style={{
            backgroundColor: colors.surface,
            borderWidth: 1,
            borderColor: colors.border,
          }}
          accessibilityLabel="Notificatie stijl instellingen"
          accessibilityRole="button"
        >
          <View className="flex-row items-center">
            <View
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: colors.primary + "20" }}
            >
              <Ionicons name="color-palette-outline" size={20} color={colors.primary} />
            </View>
            <View>
              <Text className="font-medium" style={{ color: colors.text }}>
                Notificatie Stijl
              </Text>
              <Text className="text-xs" style={{ color: colors.textSecondary }}>
                Pas de look & feel van notificaties aan
              </Text>
            </View>
          </View>
          <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
        </TouchableOpacity>

        {/* Familie Dag Notifications */}
        {groupedTemplates.familieDag.length > 0 && (
          <View className="mb-6">
            <View className="flex-row items-center mb-3">
              <Ionicons name="sunny" size={20} color="#F59E0B" />
              <Text
                className="ml-2 text-sm font-semibold uppercase tracking-wide"
                style={{ color: colors.textTertiary }}
              >
                Familie Dag
              </Text>
            </View>
            {groupedTemplates.familieDag.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                colors={colors}
                onToggle={() => handleToggleEnabled(template)}
                onEdit={() => handleEdit(template)}
                getLottieSource={getLottieSource}
              />
            ))}
          </View>
        )}

        {/* Weekend Notifications */}
        {groupedTemplates.weekend.length > 0 && (
          <View className="mb-6">
            <View className="flex-row items-center mb-3">
              <Ionicons name="bed" size={20} color="#10B981" />
              <Text
                className="ml-2 text-sm font-semibold uppercase tracking-wide"
                style={{ color: colors.textTertiary }}
              >
                Familie Weekend
              </Text>
            </View>
            {groupedTemplates.weekend.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                colors={colors}
                onToggle={() => handleToggleEnabled(template)}
                onEdit={() => handleEdit(template)}
                getLottieSource={getLottieSource}
              />
            ))}
          </View>
        )}

        {/* Birthday Notifications */}
        {groupedTemplates.birthday.length > 0 && (
          <View className="mb-6">
            <View className="flex-row items-center mb-3">
              <Ionicons name="gift" size={20} color="#F59E0B" />
              <Text
                className="ml-2 text-sm font-semibold uppercase tracking-wide"
                style={{ color: colors.textTertiary }}
              >
                Verjaardagen
              </Text>
            </View>
            {groupedTemplates.birthday.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                colors={colors}
                onToggle={() => handleToggleEnabled(template)}
                onEdit={() => handleEdit(template)}
                getLottieSource={getLottieSource}
              />
            ))}
            <View
              className="rounded-lg p-3 mt-2"
              style={{ backgroundColor: colors.surfaceSecondary }}
            >
              <Text className="text-xs" style={{ color: colors.textTertiary }}>
                💡 Tip: Gebruik {"{name}"} in de tekst om automatisch de naam van de jarige in te vullen.
              </Text>
            </View>
          </View>
        )}

        <View className="h-8" />
      </ScrollView>

      {/* Edit Template Modal */}
      <Modal visible={showEditModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
          {/* Modal Header */}
          <View
            className="flex-row items-center justify-between px-4 py-3"
            style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
          >
            <TouchableOpacity
              onPress={() => {
                setShowEditModal(false);
                setEditingTemplate(null);
              }}
            >
              <Text style={{ color: colors.textSecondary }}>Annuleren</Text>
            </TouchableOpacity>
            <Text className="font-semibold" style={{ color: colors.text }}>
              Notificatie bewerken
            </Text>
            <TouchableOpacity onPress={handleSave} disabled={isSaving}>
              {isSaving ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : (
                <Text className="font-semibold" style={{ color: colors.primary }}>
                  Opslaan
                </Text>
              )}
            </TouchableOpacity>
          </View>

          <ScrollView className="flex-1 px-4 py-6" showsVerticalScrollIndicator={false}>
            {/* Template Type */}
            {editingTemplate && (
              <View
                className="rounded-xl p-4 mb-6"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text className="text-sm font-medium" style={{ color: colors.textSecondary }}>
                  Type
                </Text>
                <Text className="mt-1 font-semibold" style={{ color: colors.text }}>
                  {getNotificationTypeName(editingTemplate.notification_type)}
                </Text>
              </View>
            )}

            {/* Title */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Titel
              </Text>
              <TextInput
                value={editTitle}
                onChangeText={setEditTitle}
                placeholder="Notificatie titel"
                placeholderTextColor={colors.textTertiary}
                className="rounded-xl px-4 py-3"
                style={{
                  backgroundColor: colors.surface,
                  borderWidth: 1,
                  borderColor: colors.border,
                  color: colors.text,
                }}
              />
            </View>

            {/* Body */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Bericht
              </Text>
              <TextInput
                value={editBody}
                onChangeText={setEditBody}
                placeholder="Notificatie bericht"
                placeholderTextColor={colors.textTertiary}
                multiline
                numberOfLines={4}
                textAlignVertical="top"
                className="rounded-xl px-4 py-3"
                style={{
                  backgroundColor: colors.surface,
                  borderWidth: 1,
                  borderColor: colors.border,
                  color: colors.text,
                  minHeight: 100,
                }}
              />
            </View>

            {/* Lottie Animation */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Animatie
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {LOTTIE_OPTIONS.map((option) => (
                  <TouchableOpacity
                    key={option.id}
                    onPress={() => setEditLottie(option.id)}
                    className="items-center mr-3"
                  >
                    <View
                      className="w-16 h-16 rounded-xl items-center justify-center"
                      style={{
                        backgroundColor: colors.surfaceSecondary,
                        borderWidth: editLottie === option.id ? 2 : 1,
                        borderColor: editLottie === option.id ? colors.primary : colors.border,
                      }}
                    >
                      <LottieView
                        source={option.file}
                        autoPlay
                        loop
                        style={{ width: 40, height: 40 }}
                      />
                    </View>
                    <Text
                      className="text-xs mt-1 text-center"
                      style={{
                        color: editLottie === option.id ? colors.primary : colors.textSecondary,
                        fontWeight: editLottie === option.id ? "600" : "400",
                      }}
                    >
                      {option.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>

            {/* Color */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Kleur
              </Text>
              <View className="flex-row flex-wrap">
                {COLOR_OPTIONS.map((option) => (
                  <TouchableOpacity
                    key={option.id}
                    onPress={() => setEditColor(option.id)}
                    className="mr-3 mb-3 items-center"
                  >
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center"
                      style={{
                        backgroundColor: option.id,
                        borderWidth: editColor === option.id ? 3 : 0,
                        borderColor: colors.text,
                      }}
                    >
                      {editColor === option.id && (
                        <Ionicons name="checkmark" size={20} color="#FFFFFF" />
                      )}
                    </View>
                    <Text
                      className="text-xs mt-1"
                      style={{ color: colors.textSecondary }}
                    >
                      {option.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Preview */}
            <View className="mb-6">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Voorbeeld
              </Text>
              <View
                className="rounded-xl p-4"
                style={{
                  backgroundColor: colors.surface,
                  borderWidth: 1,
                  borderColor: editColor,
                  borderLeftWidth: 4,
                }}
              >
                <View className="flex-row items-start">
                  <View
                    className="w-10 h-10 rounded-full items-center justify-center mr-3"
                    style={{ backgroundColor: `${editColor}20` }}
                  >
                    {getLottieSource(editLottie) && (
                      <LottieView
                        source={getLottieSource(editLottie)}
                        autoPlay
                        loop
                        style={{ width: 28, height: 28 }}
                      />
                    )}
                  </View>
                  <View className="flex-1">
                    <Text className="font-semibold" style={{ color: colors.text }}>
                      {editTitle || "Titel"}
                    </Text>
                    <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                      {editBody || "Bericht"}
                    </Text>
                  </View>
                </View>
              </View>
            </View>

            <View className="h-8" />
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Broadcast Notification Modal */}
      <Modal visible={showBroadcastModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
          {/* Modal Header */}
          <View
            className="flex-row items-center justify-between px-4 py-3"
            style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
          >
            <TouchableOpacity
              onPress={() => {
                setShowBroadcastModal(false);
                setBroadcastTitle("");
                setBroadcastBody("");
                setBroadcastLottie("Partying_Emoji");
              }}
            >
              <Text style={{ color: colors.textSecondary }}>Annuleren</Text>
            </TouchableOpacity>
            <Text className="font-semibold" style={{ color: colors.text }}>
              Notificatie versturen
            </Text>
            <TouchableOpacity onPress={handleSendBroadcast} disabled={isSendingBroadcast}>
              {isSendingBroadcast ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : (
                <Text className="font-semibold" style={{ color: "#10B981" }}>
                  Versturen
                </Text>
              )}
            </TouchableOpacity>
          </View>

          <KeyboardAvoidingView 
            behavior={Platform.OS === "ios" ? "padding" : "height"}
            className="flex-1"
          >
            <ScrollView className="flex-1 px-4 py-6" showsVerticalScrollIndicator={false}>
              {/* Info Banner */}
              <View
                className="rounded-xl p-4 mb-6 flex-row items-start"
                style={{ backgroundColor: "#10B98120" }}
              >
                <Ionicons name="megaphone" size={24} color="#10B981" />
                <View className="flex-1 ml-3">
                  <Text className="font-medium" style={{ color: "#10B981" }}>
                    Broadcast notificatie
                  </Text>
                  <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                    Deze notificatie wordt direct naar alle familieleden gestuurd als
                    in-app melding én push notificatie.
                  </Text>
                </View>
              </View>

              {/* Title */}
              <View className="mb-4">
                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  Titel *
                </Text>
                <TextInput
                  value={broadcastTitle}
                  onChangeText={setBroadcastTitle}
                  placeholder="Bijv: Belangrijke aankondiging!"
                  placeholderTextColor={colors.textTertiary}
                  className="rounded-xl px-4 py-3"
                  style={{
                    backgroundColor: colors.surface,
                    borderWidth: 1,
                    borderColor: colors.border,
                    color: colors.text,
                    fontSize: 16,
                  }}
                  maxLength={100}
                />
              </View>

              {/* Body */}
              <View className="mb-4">
                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  Bericht *
                </Text>
                <TextInput
                  value={broadcastBody}
                  onChangeText={setBroadcastBody}
                  placeholder="Schrijf hier je bericht..."
                  placeholderTextColor={colors.textTertiary}
                  multiline
                  numberOfLines={4}
                  textAlignVertical="top"
                  className="rounded-xl px-4 py-3"
                  style={{
                    backgroundColor: colors.surface,
                    borderWidth: 1,
                    borderColor: colors.border,
                    color: colors.text,
                    minHeight: 120,
                    fontSize: 16,
                  }}
                  maxLength={500}
                />
                <Text className="text-xs mt-1 text-right" style={{ color: colors.textTertiary }}>
                  {broadcastBody.length}/500
                </Text>
              </View>

              {/* Lottie Animation for In-App */}
              <View className="mb-4">
                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  In-app animatie
                </Text>
                <Text className="text-xs mb-3" style={{ color: colors.textTertiary }}>
                  Wordt getoond in de in-app notificatie banner
                </Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  {LOTTIE_OPTIONS.slice(0, 20).map((option) => (
                    <TouchableOpacity
                      key={option.id}
                      onPress={() => setBroadcastLottie(option.id)}
                      className="items-center mr-3"
                    >
                      <View
                        className="w-16 h-16 rounded-xl items-center justify-center"
                        style={{
                          backgroundColor: colors.surfaceSecondary,
                          borderWidth: broadcastLottie === option.id ? 2 : 1,
                          borderColor: broadcastLottie === option.id ? "#10B981" : colors.border,
                        }}
                      >
                        <LottieView
                          source={option.file}
                          autoPlay
                          loop
                          style={{ width: 40, height: 40 }}
                        />
                      </View>
                      <Text
                        className="text-xs mt-1 text-center"
                        style={{
                          color: broadcastLottie === option.id ? "#10B981" : colors.textSecondary,
                          fontWeight: broadcastLottie === option.id ? "600" : "400",
                          maxWidth: 64,
                        }}
                        numberOfLines={1}
                      >
                        {option.name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>

              {/* Preview */}
              <View className="mb-6">
                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  Voorbeeld
                </Text>
                <View
                  className="rounded-xl p-4"
                  style={{
                    backgroundColor: colors.surface,
                    borderWidth: 1,
                    borderColor: "#10B981",
                    borderLeftWidth: 4,
                  }}
                >
                  <View className="flex-row items-start">
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center mr-3"
                      style={{ backgroundColor: "#10B98120" }}
                    >
                      {getLottieSource(broadcastLottie) && (
                        <LottieView
                          source={getLottieSource(broadcastLottie)}
                          autoPlay
                          loop
                          style={{ width: 28, height: 28 }}
                        />
                      )}
                    </View>
                    <View className="flex-1">
                      <Text className="font-semibold" style={{ color: colors.text }}>
                        {broadcastTitle || "Titel"}
                      </Text>
                      <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                        {broadcastBody || "Bericht"}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>

              {/* Push notification info */}
              <View
                className="rounded-lg p-3 mb-6"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text className="text-xs" style={{ color: colors.textTertiary }}>
                  📱 Push notificaties worden verstuurd met de standaard app icoon.
                  Gebruikers die push notificaties hebben uitgeschakeld ontvangen alleen de in-app melding.
                </Text>
              </View>

              <View className="h-8" />
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

/**
 * Template Card Component
 */
interface TemplateCardProps {
  template: NotificationTemplate;
  colors: ReturnType<typeof useThemeColors>;
  onToggle: () => void;
  onEdit: () => void;
  getLottieSource: (id: string) => any;
}

const TemplateCard = ({
  template,
  colors,
  onToggle,
  onEdit,
  getLottieSource,
}: TemplateCardProps) => {
  const lottieSource = getLottieSource(template.lottie_animation);

  return (
    <View
      className="rounded-xl p-4 mb-3"
      style={{
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: template.is_enabled ? template.color : colors.border,
        borderLeftWidth: 4,
        opacity: template.is_enabled ? 1 : 0.6,
      }}
    >
      <View className="flex-row items-start">
        {/* Lottie Icon */}
        <View
          className="w-10 h-10 rounded-full items-center justify-center mr-3"
          style={{ backgroundColor: `${template.color}20` }}
        >
          {lottieSource ? (
            <LottieView
              source={lottieSource}
              autoPlay
              loop
              style={{ width: 28, height: 28 }}
            />
          ) : (
            <Ionicons
              name={getNotificationTypeIcon(template.notification_type)}
              size={20}
              color={template.color}
            />
          )}
        </View>

        {/* Content */}
        <View className="flex-1 mr-2">
          <Text
            className="text-xs font-medium uppercase tracking-wide mb-1"
            style={{ color: template.color }}
          >
            {getNotificationTypeName(template.notification_type).split(" - ")[1] || ""}
          </Text>
          <Text className="font-semibold" style={{ color: colors.text }} numberOfLines={1}>
            {template.title}
          </Text>
          <Text
            className="text-sm mt-1"
            style={{ color: colors.textSecondary }}
            numberOfLines={2}
          >
            {template.body}
          </Text>
        </View>

        {/* Actions */}
        <View className="items-end">
          <Switch
            value={template.is_enabled}
            onValueChange={onToggle}
            trackColor={{ false: colors.border, true: `${template.color}80` }}
            thumbColor={template.is_enabled ? template.color : colors.textTertiary}
          />
          <TouchableOpacity
            onPress={onEdit}
            className="mt-2 flex-row items-center"
          >
            <Ionicons name="pencil" size={14} color={colors.primary} />
            <Text className="text-xs ml-1" style={{ color: colors.primary }}>
              Bewerken
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

